// ═══════════════════════════════════════════════════════════════════════════
// LOUMARS THEME SWISS — SHOPIFY STOREFRONT INTEGRATION
// ═══════════════════════════════════════════════════════════════════════════

import {
  SUPPORTED_STOREFRONT_CURRENCIES,
  type StorefrontCurrencyCode,
  type StorefrontSyncConfig,
  type StorefrontCartSyncResult,
  convertStorefrontPrice,
  formatStorefrontPrice,
  getStorefrontCurrencyConfig,
  syncCartWithStorefrontApi,
  updateVariantInventory,
  getVariantInventory,
  processShopifyInventoryWebhook,
  subscribeInventoryDeprecation,
  syncInventoryToDom,
  buildShopifyCustomerAuthUrl,
  authenticateWithShopifyCustomerToken,
  getStoredCustomerSession,
  clearCustomerSession,
  syncCustomerToOrchardCore,
  subscribeCustomerSession,
  syncCustomerAccountToDom,
  DEFAULT_CUSTOMER_IDENTITY,
  type ShopifyCustomerIdentity,
  type ShopifyCustomerSession,
  type CustomerSsoLoginOptions,
  type CustomerSsoAuthResult,
  type InventoryVariantRecord,
  type InventoryDeprecationEvent,
  type ShopifyInventoryWebhookPayload,
} from '../storefront-cart-engine';

export interface ShopifyCartItem {
  id: string;
  title: string;
  price: number;
  quantity: number;
  image?: string;
  currency?: string;
  variantId?: string;
  convertedPrice?: number;
}

export interface ShopifyProductVariant {
  id: string;
  title: string;
  price: number;
  compareAtPrice?: number;
  sku?: string;
  inventoryQuantity: number;
  available: boolean;
  badgeText?: string;
  imageUrl?: string;
}

export interface ShopifyCartState {
  items: ShopifyCartItem[];
  subtotal: number;
  count: number;
  currency: string;
  currencyCode?: string;
  storefrontCartId?: string;
  syncStatus?: 'idle' | 'syncing' | 'synced' | 'error';
  lastSyncedAt?: string;
}

export let afOpenCartDrawer: () => void = () => {};
export let afCloseCartDrawer: () => void = () => {};
export let afToggleCartDrawer: () => void = () => {};
export let afAddShopifyItem: (item: any) => void = () => {};
export let afGetShopifyCart: () => ShopifyCartState = () => ({
  items: [],
  subtotal: 0,
  count: 0,
  currency: '$',
  currencyCode: 'USD',
  syncStatus: 'idle',
});
export let afClearShopifyCart: () => void = () => {};
export let afUpdateCartBadge: () => void = () => {};
export let updateCartBadge: () => void = () => {};
export let initShopifyStorefrontElements: (root?: HTMLElement | Document) => void = () => {};
export let afSelectProductVariant: (c: HTMLElement, v: string) => ShopifyProductVariant | null = () => null;
export let afInitProductVariantSelectors: (root?: HTMLElement | Document) => void = () => {};
export let selectProductVariant: (c: HTMLElement, v: string) => ShopifyProductVariant | null = () => null;
export let initProductVariantSelectors: (root?: HTMLElement | Document) => void = () => {};
const DEFAULT_EMPTY_SYNC_RESULT: StorefrontCartSyncResult = {
  success: false,
  cartId: '',
  checkoutUrl: '',
  currencyCode: 'USD',
  currencySymbol: '$',
  subtotalAmount: 0,
  totalAmount: 0,
  linesCount: 0,
  syncedAt: '',
  mode: 'offline_fallback',
};

export let afInitShopifyAppBlocks: (root?: HTMLElement | Document) => void = () => {};
export let initShopifyAppBlocks: (root?: HTMLElement | Document) => void = () => {};
export let afSetCartCurrency: (code: string) => void = () => {};
export let setCartCurrency: (code: string) => void = () => {};
export let afGetCartCurrency: () => string = () => 'USD';
export let getCartCurrency: () => string = () => 'USD';
export let afSyncStorefrontCart: (config?: StorefrontSyncConfig) => Promise<StorefrontCartSyncResult> = async () => DEFAULT_EMPTY_SYNC_RESULT;
export let syncStorefrontCart: (config?: StorefrontSyncConfig) => Promise<StorefrontCartSyncResult> = async () => DEFAULT_EMPTY_SYNC_RESULT;
export let afGetStorefrontConfig: () => StorefrontSyncConfig = () => ({});
export let afSetStorefrontConfig: (config: StorefrontSyncConfig) => void = () => {};

let afUpdateInventoryLevel: (
  variantId: string,
  quantity: number,
  options?: { locationId?: string; updatedAt?: string; previousQuantity?: number }
) => InventoryVariantRecord | null = () => null;
let updateInventoryLevel: (
  variantId: string,
  quantity: number,
  options?: { locationId?: string; updatedAt?: string; previousQuantity?: number }
) => InventoryVariantRecord | null = () => null;
let afGetInventoryLevel: (variantId: string) => InventoryVariantRecord | null = () => null;
let getInventoryLevel: (variantId: string) => InventoryVariantRecord | null = () => null;
let afProcessInventoryWebhook: (
  payload: ShopifyInventoryWebhookPayload
) => InventoryDeprecationEvent | null = () => null;
let processInventoryWebhook: (
  payload: ShopifyInventoryWebhookPayload
) => InventoryDeprecationEvent | null = () => null;
let afSubscribeInventoryUpdates: (
  callback: (e: InventoryDeprecationEvent) => void
) => () => void = () => () => {};
let subscribeInventoryUpdates: (
  callback: (e: InventoryDeprecationEvent) => void
) => () => void = () => () => {};

let afInitShopifyCustomerSso: (root?: HTMLElement | Document) => void = () => {};
let initShopifyCustomerSso: (root?: HTMLElement | Document) => void = () => {};
let afGetCustomerSession: () => ShopifyCustomerSession | null = () => null;
let getCustomerSession: () => ShopifyCustomerSession | null = () => null;
let afLoginShopifyCustomer: (params: {
  accessToken: string;
  idToken?: string;
  customer?: Partial<ShopifyCustomerIdentity>;
  orchardUserId?: string;
  expiresInSeconds?: number;
}) => ShopifyCustomerSession = authenticateWithShopifyCustomerToken;
let loginShopifyCustomer: (params: {
  accessToken: string;
  idToken?: string;
  customer?: Partial<ShopifyCustomerIdentity>;
  orchardUserId?: string;
  expiresInSeconds?: number;
}) => ShopifyCustomerSession = authenticateWithShopifyCustomerToken;
let afLogoutShopifyCustomer: () => void = clearCustomerSession;
let logoutShopifyCustomer: () => void = clearCustomerSession;
let afSubscribeCustomerAuth: (
  listener: (s: ShopifyCustomerSession | null) => void
) => () => void = subscribeCustomerSession;
let subscribeCustomerAuth: (
  listener: (s: ShopifyCustomerSession | null) => void
) => () => void = subscribeCustomerSession;
let afSyncCustomerToOrchardCore: (
  session?: ShopifyCustomerSession | null
) => Promise<CustomerSsoAuthResult> = syncCustomerToOrchardCore;
let syncCustomerToOrchardCoreBridge: (
  session?: ShopifyCustomerSession | null
) => Promise<CustomerSsoAuthResult> = syncCustomerToOrchardCore;

let activeStorefrontCurrencyCode: StorefrontCurrencyCode = 'USD';
let activeStorefrontCartId: string | null = null;
let activeStorefrontConfig: StorefrontSyncConfig = {};
let cartSyncStatus: 'idle' | 'syncing' | 'synced' | 'error' = 'idle';
let lastSyncedTimestamp: string | null = null;

let shopifyCartItems: ShopifyCartItem[] = [];

function getShopifyCartState(currencyOverride?: string): ShopifyCartState {
  const code = activeStorefrontCurrencyCode;
  const config = getStorefrontCurrencyConfig(code);
  const currencySymbol = currencyOverride || config.symbol;

  const convertedItems = shopifyCartItems.map((it) => ({
    ...it,
    currency: currencySymbol,
    convertedPrice: convertStorefrontPrice(it.price, code),
  }));
  const subtotal = convertedItems.reduce(
    (acc, it) => acc + (it.convertedPrice !== undefined ? it.convertedPrice : it.price) * it.quantity,
    0
  );
  const count = shopifyCartItems.reduce((acc, it) => acc + it.quantity, 0);
  return {
    items: convertedItems,
    subtotal: Math.round(subtotal * 100) / 100,
    count,
    currency: currencySymbol,
    currencyCode: code,
    storefrontCartId: activeStorefrontCartId || undefined,
    syncStatus: cartSyncStatus,
    lastSyncedAt: lastSyncedTimestamp || undefined,
  };
}

  let lastActiveCartTrigger: HTMLElement | null = null;

  function updateCartBadgeInternal(): void {
    const state = getShopifyCartState();
    if (typeof document !== 'undefined') {
      const badges = document.querySelectorAll<HTMLElement>(
        '[data-cart-drawer-badge], [data-cart-drawer-count]'
      );
      badges.forEach((b) => {
        b.textContent = String(state.count);
      });
    }
  }

  function renderCartDrawer(drawerEl: HTMLElement): void {
    const config = getStorefrontCurrencyConfig(activeStorefrontCurrencyCode);
    const currency = config.symbol;
    const thresholdAttr = drawerEl.getAttribute('data-free-shipping-threshold');
    const baseThreshold = thresholdAttr ? parseFloat(thresholdAttr) || 150 : 150;
    const threshold = convertStorefrontPrice(baseThreshold, activeStorefrontCurrencyCode);
    const state = getShopifyCartState(currency);

    const countBadge = drawerEl.querySelector('[data-cart-drawer-count]');
    if (countBadge) countBadge.textContent = String(state.count);

    const subtotalEl = drawerEl.querySelector('[data-cart-drawer-subtotal]');
    if (subtotalEl) subtotalEl.textContent = `${currency}${state.subtotal.toFixed(config.fractionDigits)}`;

    const currencySelect = drawerEl.querySelector<HTMLSelectElement>('[data-cart-currency-select]');
    if (currencySelect) {
      currencySelect.value = activeStorefrontCurrencyCode;
    }

    const currencyButtons = drawerEl.querySelectorAll<HTMLElement>('[data-cart-currency-option]');
    currencyButtons.forEach((btn) => {
      const code = btn.getAttribute('data-cart-currency-code');
      const isMatch = code?.toUpperCase() === activeStorefrontCurrencyCode;
      btn.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
      if (isMatch) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });

    const syncStatusEl = drawerEl.querySelector<HTMLElement>('[data-cart-sync-status]');
    if (syncStatusEl) {
      syncStatusEl.textContent = cartSyncStatus === 'syncing' ? 'Syncing...' : 'Synced';
    }

    const progressBar = drawerEl.querySelector<HTMLElement>('[data-shipping-progress-bar]');
    const progressPercent = drawerEl.querySelector('[data-shipping-progress-percent]');
    const progressText = drawerEl.querySelector('[data-shipping-progress-text]');

    const pct = Math.min(100, Math.round((state.subtotal / threshold) * 100));
    if (progressBar) progressBar.style.width = `${pct}%`;
    if (progressPercent) progressPercent.textContent = `${pct}%`;
    if (progressText) {
      if (state.subtotal >= threshold) {
        progressText.textContent = 'Complimentary tier unlocked!';
      } else {
        const remaining = (threshold - state.subtotal).toFixed(config.fractionDigits);
        progressText.textContent = `Add ${currency}${remaining} for complimentary delivery`;
      }
    }

    const emptyState = drawerEl.querySelector<HTMLElement>('[data-cart-drawer-empty]');
    const itemsContainer = drawerEl.querySelector<HTMLElement>('[data-cart-drawer-items]');

    if (itemsContainer) {
      if (state.items.length === 0) {
        itemsContainer.innerHTML = '';
        if (emptyState) emptyState.classList.remove('hidden');
      } else {
        if (emptyState) emptyState.classList.add('hidden');
        itemsContainer.innerHTML = state.items
          .map(
            (item) => `
          <div class="cart-item-row flex items-center justify-between py-3 gap-3 border-b border-neutral-200 dark:border-neutral-800" data-item-id="${item.id}">
            ${item.image ? `<img src="${item.image}" alt="${item.title}" class="w-12 h-12 object-cover rounded shrink-0 border border-neutral-200 dark:border-neutral-700" />` : ''}
            <div class="flex-1 min-w-0">
              <h4 class="text-sm font-semibold truncate text-neutral-900 dark:text-neutral-100">${item.title}</h4>
              <span class="text-xs font-mono text-neutral-600 dark:text-neutral-400">${currency}${(item.convertedPrice !== undefined ? item.convertedPrice : item.price).toFixed(config.fractionDigits)}</span>
            </div>
            <div class="flex items-center gap-1.5 shrink-0">
              <button type="button" class="w-7 h-7 flex items-center justify-center border border-neutral-300 dark:border-neutral-700 rounded text-xs font-bold text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800" data-cart-qty-decrease="${item.id}" aria-label="Decrease quantity">−</button>
              <span class="w-6 text-center font-mono text-xs font-semibold text-neutral-900 dark:text-neutral-100">${item.quantity}</span>
              <button type="button" class="w-7 h-7 flex items-center justify-center border border-neutral-300 dark:border-neutral-700 rounded text-xs font-bold text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800" data-cart-qty-increase="${item.id}" aria-label="Increase quantity">+</button>
              <button type="button" class="ml-2 text-xs text-neutral-400 hover:text-[#D90429] p-1" data-cart-item-remove="${item.id}" aria-label="Remove item">✕</button>
            </div>
          </div>
        `
          )
          .join('');
      }
    }

    updateCartBadgeInternal();
  }

  function openCartDrawer(): void {
    const drawerEl = document.querySelector<HTMLElement>('[data-shopify-cart-drawer]');
    if (!drawerEl) return;
    if (typeof document !== 'undefined') {
      lastActiveCartTrigger = document.activeElement as HTMLElement | null;
    }
    drawerEl.classList.remove('pointer-events-none');
    drawerEl.setAttribute('aria-hidden', 'false');

    const backdrop = drawerEl.querySelector<HTMLElement>('[data-cart-drawer-backdrop]');
    if (backdrop) {
      backdrop.classList.remove('opacity-0', 'pointer-events-none');
      backdrop.classList.add('opacity-100');
    }

    const panel = drawerEl.querySelector<HTMLElement>('[data-cart-drawer-panel]');
    if (panel) {
      panel.classList.remove('translate-x-full');
    }

    renderCartDrawer(drawerEl);
    const closeBtn = drawerEl.querySelector<HTMLElement>('[data-cart-drawer-close]');
    if (closeBtn && typeof closeBtn.focus === 'function') {
      closeBtn.focus();
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('loumars:cart-drawer:opened'));
    }
  }

  function closeCartDrawer(): void {
    const drawerEl = document.querySelector<HTMLElement>('[data-shopify-cart-drawer]');
    if (!drawerEl) return;
    drawerEl.setAttribute('aria-hidden', 'true');
    drawerEl.classList.add('pointer-events-none');

    const backdrop = drawerEl.querySelector<HTMLElement>('[data-cart-drawer-backdrop]');
    if (backdrop) {
      backdrop.classList.remove('opacity-100');
      backdrop.classList.add('opacity-0', 'pointer-events-none');
    }

    const panel = drawerEl.querySelector<HTMLElement>('[data-cart-drawer-panel]');
    if (panel) {
      panel.classList.add('translate-x-full');
    }

    if (lastActiveCartTrigger && typeof lastActiveCartTrigger.focus === 'function') {
      lastActiveCartTrigger.focus();
      lastActiveCartTrigger = null;
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('loumars:cart-drawer:closed'));
    }
  }

  function toggleCartDrawer(): void {
    const drawerEl = document.querySelector<HTMLElement>('[data-shopify-cart-drawer]');
    if (!drawerEl) return;
    const isHidden =
      drawerEl.getAttribute('aria-hidden') === 'true' ||
      drawerEl.classList.contains('pointer-events-none');
    if (isHidden) {
      openCartDrawer();
    } else {
      closeCartDrawer();
    }
  }

  function addShopifyItem(item: {
    id: string;
    title: string;
    price: number;
    quantity?: number;
    image?: string;
    currency?: string;
    variantId?: string;
  }): void {
    const qty = item.quantity && item.quantity > 0 ? item.quantity : 1;
    const existing = shopifyCartItems.find((it) => it.id === item.id);
    if (existing) {
      existing.quantity += qty;
    } else {
      shopifyCartItems.push({
        id: item.id,
        title: item.title,
        price: item.price,
        quantity: qty,
        image: item.image,
        currency: item.currency,
        variantId: item.variantId,
      });
    }

    const drawerEl = document.querySelector<HTMLElement>('[data-shopify-cart-drawer]');
    if (drawerEl) {
      renderCartDrawer(drawerEl);
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('loumars:cart-updated', {
          detail: { items: shopifyCartItems, cart: getShopifyCartState() },
        })
      );
    }
    openCartDrawer();
  }

  function updateShopifyItemQuantity(id: string, quantity: number): void {
    if (quantity <= 0) {
      shopifyCartItems = shopifyCartItems.filter((it) => it.id !== id);
    } else {
      const item = shopifyCartItems.find((it) => it.id === id);
      if (item) item.quantity = quantity;
    }
    const drawerEl = document.querySelector<HTMLElement>('[data-shopify-cart-drawer]');
    if (drawerEl) renderCartDrawer(drawerEl);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('loumars:cart-updated', {
          detail: { items: shopifyCartItems, cart: getShopifyCartState() },
        })
      );
    }
  }

  function removeShopifyItem(id: string): void {
    updateShopifyItemQuantity(id, 0);
  }

  function clearShopifyCart(): void {
    shopifyCartItems = [];
    const drawerEl = document.querySelector<HTMLElement>('[data-shopify-cart-drawer]');
    if (drawerEl) renderCartDrawer(drawerEl);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('loumars:cart-updated', {
          detail: { items: [], cart: getShopifyCartState() },
        })
      );
    }
  }

  function setCartCurrencyInternal(code: string): void {
    if (!code) return;
    const upper = code.toUpperCase() as StorefrontCurrencyCode;
    if (!SUPPORTED_STOREFRONT_CURRENCIES[upper]) return;
    activeStorefrontCurrencyCode = upper;
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('loumars_cart_currency', upper);
      } catch {}
    }
    if (typeof document !== 'undefined') {
      const drawers = document.querySelectorAll<HTMLElement>('[data-shopify-cart-drawer]');
      drawers.forEach((drawer) => renderCartDrawer(drawer));
    }
    updateCartBadgeInternal();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('loumars:cart-currency-changed', {
          detail: {
            currency: getStorefrontCurrencyConfig(upper).symbol,
            currencyCode: upper,
            cart: getShopifyCartState(),
          },
        })
      );
      window.dispatchEvent(
        new CustomEvent('loumars:cart-updated', {
          detail: { items: shopifyCartItems, cart: getShopifyCartState() },
        })
      );
    }
    // Asynchronously synchronize Storefront cart in background
    syncStorefrontCartInternal().catch(() => {});
  }

  async function syncStorefrontCartInternal(
    config?: StorefrontSyncConfig
  ): Promise<StorefrontCartSyncResult> {
    cartSyncStatus = 'syncing';
    if (config) {
      activeStorefrontConfig = { ...activeStorefrontConfig, ...config };
    }
    try {
      const res = await syncCartWithStorefrontApi(
        shopifyCartItems.map((it) => ({
          id: it.id,
          quantity: it.quantity,
          price: it.price,
          title: it.title,
          variantId: it.variantId,
          image: it.image,
        })),
        {
          ...activeStorefrontConfig,
          currencyCode: activeStorefrontCurrencyCode,
          cartId: activeStorefrontCartId || undefined,
        }
      );
      activeStorefrontCartId = res.cartId;
      cartSyncStatus = res.success ? 'synced' : 'error';
      lastSyncedTimestamp = res.syncedAt;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('loumars:cart-synchronized', {
            detail: { result: res, cart: getShopifyCartState() },
          })
        );
      }
      return res;
    } catch (err) {
      cartSyncStatus = 'error';
      throw err;
    }
  }

  function initShopifyStorefrontElementsInternal(root: HTMLElement | Document = document): void {
    if (typeof localStorage !== 'undefined') {
      try {
        const savedCurrency = localStorage.getItem('loumars_cart_currency');
        if (savedCurrency && SUPPORTED_STOREFRONT_CURRENCIES[savedCurrency as StorefrontCurrencyCode]) {
          activeStorefrontCurrencyCode = savedCurrency as StorefrontCurrencyCode;
        }
      } catch {}
    }

    updateCartBadgeInternal();

    const currencySelects = root.querySelectorAll<HTMLSelectElement>('[data-cart-currency-select]');
    currencySelects.forEach((sel) => {
      if (sel.dataset.bound) return;
      sel.dataset.bound = 'true';
      sel.value = activeStorefrontCurrencyCode;
      sel.addEventListener('change', () => {
        setCartCurrencyInternal(sel.value);
      });
    });

    const currencyButtons = root.querySelectorAll<HTMLElement>('[data-cart-currency-option]');
    currencyButtons.forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const code = btn.getAttribute('data-cart-currency-code');
        if (code) setCartCurrencyInternal(code);
      });
    });

    const triggers = root.querySelectorAll<HTMLElement>('[data-cart-drawer-trigger]');
    triggers.forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openCartDrawer();
      });
    });

    if (typeof window !== 'undefined') {
      const w = window as unknown as Record<string, unknown>;
      w.afUpdateInventoryLevel = updateVariantInventory;
      w.updateInventoryLevel = updateVariantInventory;
      w.afGetInventoryLevel = getVariantInventory;
      w.getInventoryLevel = getVariantInventory;
      w.afProcessInventoryWebhook = processShopifyInventoryWebhook;
      w.processInventoryWebhook = processShopifyInventoryWebhook;
      w.afSubscribeInventoryUpdates = subscribeInventoryDeprecation;
      w.subscribeInventoryUpdates = subscribeInventoryDeprecation;

      if (!w.__loumars_inventory_listener_bound) {
        w.__loumars_inventory_listener_bound = true;
        window.addEventListener('loumars:inventory-updated', (e: Event) => {
          const detail = (e as CustomEvent).detail;
          if (detail?.variantId && typeof detail.inventoryQuantity === 'number') {
            syncInventoryToDom(detail.variantId, detail.inventoryQuantity, root);
          }
        });
      }
    }

    const drawer = root.querySelector<HTMLElement>('[data-shopify-cart-drawer]');
    if (drawer) {
      renderCartDrawer(drawer);

      const adminLinks = drawer.querySelectorAll<HTMLAnchorElement>(
        '[data-admin-link], a[href*="/admin" i], a[href*="/Admin"]'
      );
      adminLinks.forEach((link) => {
        if (link.dataset.bound) return;
        link.dataset.bound = 'true';
        link.addEventListener('click', (e) => {
          const href = link.getAttribute('href');
          if (href) {
            e.preventDefault();
            if (typeof window !== 'undefined') {
              window.location.href = href;
            }
          }
        });
      });

      const closeBtn = drawer.querySelector<HTMLElement>('[data-cart-drawer-close]');
      if (closeBtn && !closeBtn.dataset.bound) {
        closeBtn.dataset.bound = 'true';
        closeBtn.addEventListener('click', closeCartDrawer);
      }

      const backdrop = drawer.querySelector<HTMLElement>('[data-cart-drawer-backdrop]');
      if (backdrop && !backdrop.dataset.bound) {
        backdrop.dataset.bound = 'true';
        backdrop.addEventListener('click', closeCartDrawer);
      }

      const continueBtn = drawer.querySelector<HTMLElement>('[data-cart-drawer-continue]');
      if (continueBtn && !continueBtn.dataset.bound) {
        continueBtn.dataset.bound = 'true';
        continueBtn.addEventListener('click', closeCartDrawer);
      }

      const itemsContainer = drawer.querySelector<HTMLElement>('[data-cart-drawer-items]');
      if (itemsContainer && !itemsContainer.dataset.bound) {
        itemsContainer.dataset.bound = 'true';
        itemsContainer.addEventListener('click', (e) => {
          const target = (e.target as HTMLElement).closest('button');
          if (!target) return;

          const decreaseId = target.getAttribute('data-cart-qty-decrease');
          if (decreaseId) {
            const item = shopifyCartItems.find((it) => it.id === decreaseId);
            if (item) updateShopifyItemQuantity(decreaseId, item.quantity - 1);
            return;
          }

          const increaseId = target.getAttribute('data-cart-qty-increase');
          if (increaseId) {
            const item = shopifyCartItems.find((it) => it.id === increaseId);
            if (item) updateShopifyItemQuantity(increaseId, item.quantity + 1);
            return;
          }

          const removeId = target.getAttribute('data-cart-item-remove');
          if (removeId) {
            removeShopifyItem(removeId);
            return;
          }
        });
      }
    }

    if (typeof window !== 'undefined' && !window.afCartKeydownBound) {
      window.afCartKeydownBound = true;
      window.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          closeCartDrawer();
        }
      });
    }

    const buyButtons = root.querySelectorAll<HTMLElement>('[data-shopify-buy-button]');
    buyButtons.forEach((card) => {
      if (card.dataset.bound) return;
      card.dataset.bound = 'true';

      const qtyInput = card.querySelector<HTMLInputElement>('[data-quantity-input]');
      const decBtn = card.querySelector<HTMLButtonElement>('[data-quantity-decrease]');
      const incBtn = card.querySelector<HTMLButtonElement>('[data-quantity-increase]');
      const addBtn = card.querySelector<HTMLButtonElement>('[data-add-to-cart]');
      const directBtn = card.querySelector<HTMLButtonElement>('[data-direct-checkout]');

      if (decBtn && qtyInput) {
        decBtn.addEventListener('click', () => {
          const val = parseInt(qtyInput.value, 10) || 1;
          if (val > 1) qtyInput.value = String(val - 1);
        });
      }

      if (incBtn && qtyInput) {
        incBtn.addEventListener('click', () => {
          const val = parseInt(qtyInput.value, 10) || 1;
          if (val < 99) qtyInput.value = String(val + 1);
        });
      }

      if (addBtn) {
        addBtn.addEventListener('click', () => {
          const productId = card.getAttribute('data-product-id') || 'prod';
          const title = card.getAttribute('data-product-title') || 'Product';
          const price = parseFloat(card.getAttribute('data-price') || '0') || 0;
          const currency = card.getAttribute('data-currency') || '$';
          const image = card.getAttribute('data-image') || undefined;
          const variantId = card.getAttribute('data-variant-id') || undefined;
          const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

          addShopifyItem({
            id: productId,
            title,
            price,
            quantity: qty,
            image,
            currency,
            variantId,
          });
        });
      }

      if (directBtn) {
        directBtn.addEventListener('click', () => {
          const checkoutUrl = card.getAttribute('data-checkout-url') || '/checkout';
          const productId = card.getAttribute('data-product-id') || 'prod';
          const title = card.getAttribute('data-product-title') || 'Product';
          const price = parseFloat(card.getAttribute('data-price') || '0') || 0;
          const currency = card.getAttribute('data-currency') || '$';
          const image = card.getAttribute('data-image') || undefined;
          const variantId = card.getAttribute('data-variant-id') || undefined;
          const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

          addShopifyItem({
            id: productId,
            title,
            price,
            quantity: qty,
            image,
            currency,
            variantId,
          });

          if (typeof window !== 'undefined' && checkoutUrl) {
            window.location.href = checkoutUrl;
          }
        });
      }
    });

    initProductVariantSelectors(root);
    initShopifyAppBlocksInternal(root);
    initShopifyCustomerSsoInternal(root);
  }

  function selectProductVariantInternal(
    container: HTMLElement,
    variantId: string
  ): ShopifyProductVariant | null {
    const selector = container.hasAttribute('data-shopify-variant-selector')
      ? container
      : container.querySelector<HTMLElement>('[data-shopify-variant-selector]');
    if (!selector) return null;

    const optionBtns = selector.querySelectorAll<HTMLElement>('[data-variant-option]');
    let matchedOption: HTMLElement | null = null;
    let selectedVariant: ShopifyProductVariant | null = null;

    for (const btn of Array.from(optionBtns)) {
      const id = btn.getAttribute('data-variant-id');
      const isTarget = id === variantId;
      btn.setAttribute('aria-pressed', isTarget ? 'true' : 'false');

      if (isTarget) {
        matchedOption = btn;
        const price = parseFloat(btn.getAttribute('data-variant-price') || '0') || 0;
        const comparePriceAttr = btn.getAttribute('data-variant-compare-price');
        const compareAtPrice = comparePriceAttr
          ? parseFloat(comparePriceAttr) || undefined
          : undefined;
        const inventory = parseInt(btn.getAttribute('data-variant-inventory') || '0', 10) || 0;
        const available = btn.getAttribute('data-variant-available') === 'true' && inventory > 0;
        const title =
          btn.getAttribute('data-variant-title') ||
          btn.getAttribute('data-variant-name') ||
          id ||
          'Variant';
        const badgeText =
          btn.getAttribute('data-variant-badge') ||
          (available ? `In Stock (${inventory} units)` : 'Sold Out');
        const sku = btn.getAttribute('data-variant-sku') || undefined;
        const imageUrl = btn.getAttribute('data-variant-image') || undefined;

        selectedVariant = {
          id: variantId,
          title,
          price,
          compareAtPrice,
          sku,
          inventoryQuantity: inventory,
          available,
          badgeText,
          imageUrl,
        };
      }
    }

    if (!matchedOption || !selectedVariant) return null;

    const currency = selector.getAttribute('data-currency') || '$';
    const priceDisplay = selector.querySelector<HTMLElement>('[data-variant-price-display]');
    if (priceDisplay) {
      priceDisplay.textContent = `${currency}${selectedVariant.price.toFixed(2)}`;
    }

    const comparePriceDisplay = selector.querySelector<HTMLElement>(
      '[data-variant-compare-price-display]'
    );
    if (comparePriceDisplay) {
      if (selectedVariant.compareAtPrice) {
        comparePriceDisplay.textContent = `${currency}${selectedVariant.compareAtPrice.toFixed(2)}`;
        comparePriceDisplay.style.display = '';
      } else {
        comparePriceDisplay.textContent = '';
        comparePriceDisplay.style.display = 'none';
      }
    }

    const selectedName = selector.querySelector<HTMLElement>('[data-selected-variant-name]');
    if (selectedName) {
      selectedName.textContent = selectedVariant.title;
    }

    const inventoryBadge = selector.querySelector<HTMLElement>('[data-inventory-badge]');
    if (inventoryBadge) {
      const textEl = inventoryBadge.querySelector('[data-inventory-text]') || inventoryBadge;
      textEl.textContent =
        selectedVariant.badgeText || (selectedVariant.available ? 'In Stock' : 'Sold Out');

      const dot = inventoryBadge.querySelector<HTMLElement>('.rounded-full');
      if (dot) {
        if (!selectedVariant.available || selectedVariant.inventoryQuantity <= 0) {
          dot.className = 'inline-block w-2 h-2 rounded-full bg-red-500';
        } else if (selectedVariant.inventoryQuantity <= 5) {
          dot.className = 'inline-block w-2 h-2 rounded-full bg-amber-400';
        } else {
          dot.className = 'inline-block w-2 h-2 rounded-full bg-emerald-500';
        }
      }
    }

    // Synchronize with parent BuyButton card if nested or linked
    const buyCard =
      container.closest<HTMLElement>('[data-shopify-buy-button]') ||
      (container.hasAttribute('data-shopify-buy-button') ? container : null) ||
      document.querySelector<HTMLElement>(
        `[data-shopify-buy-button][data-product-id="${selector.getAttribute('data-product-id')}"]`
      );

    if (buyCard) {
      buyCard.setAttribute('data-variant-id', selectedVariant.id);
      buyCard.setAttribute('data-price', String(selectedVariant.price));
      if (selectedVariant.imageUrl) {
        buyCard.setAttribute('data-image', selectedVariant.imageUrl);
        const img = buyCard.querySelector<HTMLImageElement>('img');
        if (img) img.src = selectedVariant.imageUrl;
      }

      const buyPriceEl = buyCard.querySelector<HTMLElement>('[data-buy-price]');
      if (buyPriceEl) {
        buyPriceEl.textContent = `${currency}${selectedVariant.price.toFixed(2)}`;
      }

      const addBtn = buyCard.querySelector<HTMLButtonElement>('[data-add-to-cart]');
      if (addBtn) {
        const btnText = addBtn.querySelector('span');
        if (selectedVariant.available && selectedVariant.inventoryQuantity > 0) {
          addBtn.disabled = false;
          addBtn.classList.remove('opacity-50', 'cursor-not-allowed');
          if (btnText && btnText.textContent?.toLowerCase().includes('sold')) {
            btnText.textContent = 'Add to Cart';
          }
        } else {
          addBtn.disabled = true;
          addBtn.classList.add('opacity-50', 'cursor-not-allowed');
          if (btnText) {
            btnText.textContent = 'Sold Out';
          }
        }
      }

      const directBtn = buyCard.querySelector<HTMLButtonElement>('[data-direct-checkout]');
      if (directBtn) {
        directBtn.disabled = !selectedVariant.available || selectedVariant.inventoryQuantity <= 0;
        if (directBtn.disabled) {
          directBtn.classList.add('opacity-50', 'cursor-not-allowed');
        } else {
          directBtn.classList.remove('opacity-50', 'cursor-not-allowed');
        }
      }
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('loumars:variant-selected', {
          detail: {
            productId: selector.getAttribute('data-product-id') || '',
            variantId: selectedVariant.id,
            variant: selectedVariant,
          },
        })
      );
    }

    return selectedVariant;
  }

  function initProductVariantSelectorsInternal(root: HTMLElement | Document = document): void {
    const selectors = root.querySelectorAll<HTMLElement>('[data-shopify-variant-selector]');
    selectors.forEach((sel) => {
      if (sel.dataset.variantBound === 'true') return;
      sel.dataset.variantBound = 'true';

      const optionBtns = sel.querySelectorAll<HTMLElement>('[data-variant-option]');
      optionBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const variantId = btn.getAttribute('data-variant-id');
          if (variantId) {
            selectProductVariantInternal(sel, variantId);
          }
        });
      });
    });
  }

  function initShopifyAppBlocksInternal(root: HTMLElement | Document = document): void {
    const blocks = root.querySelectorAll<HTMLElement>('[data-shopify-app-block]');
    blocks.forEach((block) => {
      if (block.dataset.appBlockBound === 'true') return;
      block.dataset.appBlockBound = 'true';

      const qtyInput = block.querySelector<HTMLInputElement>('[data-quantity-input]');
      const decBtn = block.querySelector<HTMLButtonElement>('[data-quantity-decrease]');
      const incBtn = block.querySelector<HTMLButtonElement>('[data-quantity-increase]');
      const addBtn = block.querySelector<HTMLButtonElement>('[data-add-to-cart]');
      const directBtn = block.querySelector<HTMLButtonElement>('[data-direct-checkout]');
      const drawerTrigger = block.querySelector<HTMLButtonElement>('[data-cart-drawer-trigger]');

      if (decBtn && qtyInput) {
        decBtn.addEventListener('click', () => {
          const val = parseInt(qtyInput.value, 10) || 1;
          if (val > 1) qtyInput.value = String(val - 1);
        });
      }

      if (incBtn && qtyInput) {
        incBtn.addEventListener('click', () => {
          const val = parseInt(qtyInput.value, 10) || 1;
          if (val < 99) qtyInput.value = String(val + 1);
        });
      }

      if (addBtn) {
        addBtn.addEventListener('click', () => {
          const productId = block.getAttribute('data-product-id') || 'prod';
          const title = block.getAttribute('data-product-title') || 'Product';
          const price = parseFloat(block.getAttribute('data-price') || '0') || 0;
          const currency = block.getAttribute('data-currency') || '$';
          const image = block.getAttribute('data-image') || undefined;
          const variantId = block.getAttribute('data-variant-id') || undefined;
          const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

          addShopifyItem({
            id: productId,
            title,
            price,
            quantity: qty,
            image,
            currency,
            variantId,
          });
        });
      }

      if (directBtn) {
        directBtn.addEventListener('click', () => {
          const checkoutUrl = block.getAttribute('data-checkout-url') || '/checkout';
          const productId = block.getAttribute('data-product-id') || 'prod';
          const title = block.getAttribute('data-product-title') || 'Product';
          const price = parseFloat(block.getAttribute('data-price') || '0') || 0;
          const currency = block.getAttribute('data-currency') || '$';
          const image = block.getAttribute('data-image') || undefined;
          const variantId = block.getAttribute('data-variant-id') || undefined;
          const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

          addShopifyItem({
            id: productId,
            title,
            price,
            quantity: qty,
            image,
            currency,
            variantId,
          });

          if (typeof window !== 'undefined' && checkoutUrl) {
            window.location.href = checkoutUrl;
          }
        });
      }

      if (drawerTrigger) {
        drawerTrigger.addEventListener('click', (e) => {
          e.preventDefault();
          openCartDrawer();
        });
      }
    });

    initProductVariantSelectors(root);
  }

  function initShopifyCustomerSsoInternal(root: HTMLElement | Document = document): void {
    syncCustomerAccountToDom(getStoredCustomerSession());

    const accountRoots = root.querySelectorAll<HTMLElement>('[data-shopify-customer-account]');
    accountRoots.forEach((container) => {
      if (container.dataset.boundCustomerSso) return;
      container.dataset.boundCustomerSso = 'true';

      const loginBtn = container.querySelector<HTMLElement>('[data-customer-login-button]');
      if (loginBtn && !loginBtn.dataset.bound) {
        loginBtn.dataset.bound = 'true';
        loginBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const shop = container.getAttribute('data-shop-domain') || undefined;
          const clientId = container.getAttribute('data-client-id') || undefined;
          const redirectUri = container.getAttribute('data-redirect-uri') || undefined;
          const authUrl = buildShopifyCustomerAuthUrl({ shopDomain: shop, clientId, redirectUri });

          if (typeof window !== 'undefined') {
            if (window.location.search.includes('sso_mock=true') || window.location.hostname === 'localhost') {
              authenticateWithShopifyCustomerToken({
                accessToken: `shpca_${Date.now()}_token`,
                customer: { ...DEFAULT_CUSTOMER_IDENTITY },
              });
            } else {
              window.location.href = authUrl;
            }
          }
        });
      }

      const logoutBtn = container.querySelector<HTMLElement>('[data-customer-logout-button]');
      if (logoutBtn && !logoutBtn.dataset.bound) {
        logoutBtn.dataset.bound = 'true';
        logoutBtn.addEventListener('click', (e) => {
          e.preventDefault();
          clearCustomerSession();
        });
      }

      const syncBtn = container.querySelector<HTMLElement>('[data-customer-sso-sync]');
      if (syncBtn && !syncBtn.dataset.bound) {
        syncBtn.dataset.bound = 'true';
        syncBtn.addEventListener('click', async (e) => {
          e.preventDefault();
          await syncCustomerToOrchardCore();
        });
      }
    });

    if (typeof window !== 'undefined') {
      const w = window as unknown as Record<string, unknown>;
      w.afInitShopifyCustomerSso = initShopifyCustomerSsoInternal;
      w.initShopifyCustomerSso = initShopifyCustomerSsoInternal;
      w.afGetCustomerSession = getStoredCustomerSession;
      w.getCustomerSession = getStoredCustomerSession;
      w.afLoginShopifyCustomer = authenticateWithShopifyCustomerToken;
      w.loginShopifyCustomer = authenticateWithShopifyCustomerToken;
      w.afLogoutShopifyCustomer = clearCustomerSession;
      w.logoutShopifyCustomer = clearCustomerSession;
      w.afSubscribeCustomerAuth = subscribeCustomerSession;
      w.subscribeCustomerAuth = subscribeCustomerSession;
      w.afSyncCustomerToOrchardCore = syncCustomerToOrchardCore;
      w.syncCustomerToOrchardCore = syncCustomerToOrchardCore;

      if (!w.__loumars_customer_sso_bound) {
        w.__loumars_customer_sso_bound = true;
        window.addEventListener('loumars:customer-sso-changed', () => {
          syncCustomerAccountToDom(getStoredCustomerSession());
        });
      }
    }
  }

  afOpenCartDrawer = openCartDrawer;
  afCloseCartDrawer = closeCartDrawer;
  afToggleCartDrawer = toggleCartDrawer;
  afAddShopifyItem = addShopifyItem;
  afGetShopifyCart = () => getShopifyCartState();
  afClearShopifyCart = clearShopifyCart;
  afUpdateCartBadge = updateCartBadgeInternal;
  updateCartBadge = updateCartBadgeInternal;
  initShopifyStorefrontElements = initShopifyStorefrontElementsInternal;
  afSelectProductVariant = selectProductVariantInternal;
  afInitProductVariantSelectors = initProductVariantSelectorsInternal;
  selectProductVariant = selectProductVariantInternal;
  initProductVariantSelectors = initProductVariantSelectorsInternal;
  afInitShopifyAppBlocks = initShopifyAppBlocksInternal;
  initShopifyAppBlocks = initShopifyAppBlocksInternal;
  afSetCartCurrency = setCartCurrencyInternal;
  setCartCurrency = setCartCurrencyInternal;
  afGetCartCurrency = () => activeStorefrontCurrencyCode;
  getCartCurrency = () => activeStorefrontCurrencyCode;
  afSyncStorefrontCart = syncStorefrontCartInternal;
  syncStorefrontCart = syncStorefrontCartInternal;
  afGetStorefrontConfig = () => ({ ...activeStorefrontConfig });
  afSetStorefrontConfig = (cfg: StorefrontSyncConfig) => {
    activeStorefrontConfig = { ...activeStorefrontConfig, ...cfg };
  };

  afUpdateInventoryLevel = updateVariantInventory;
  updateInventoryLevel = updateVariantInventory;
  afGetInventoryLevel = getVariantInventory;
  getInventoryLevel = getVariantInventory;
  afProcessInventoryWebhook = processShopifyInventoryWebhook;
  processInventoryWebhook = processShopifyInventoryWebhook;
  afSubscribeInventoryUpdates = subscribeInventoryDeprecation;
  subscribeInventoryUpdates = subscribeInventoryDeprecation;

  afInitShopifyCustomerSso = initShopifyCustomerSsoInternal;
  initShopifyCustomerSso = initShopifyCustomerSsoInternal;
  afGetCustomerSession = getStoredCustomerSession;
  getCustomerSession = getStoredCustomerSession;
  afLoginShopifyCustomer = authenticateWithShopifyCustomerToken;
  loginShopifyCustomer = authenticateWithShopifyCustomerToken;
  afLogoutShopifyCustomer = clearCustomerSession;
  logoutShopifyCustomer = clearCustomerSession;
  afSubscribeCustomerAuth = subscribeCustomerSession;
  subscribeCustomerAuth = subscribeCustomerSession;
  afSyncCustomerToOrchardCore = syncCustomerToOrchardCore;
  syncCustomerToOrchardCoreBridge = syncCustomerToOrchardCore;

export {
  getShopifyCartState,
  openCartDrawer,
  closeCartDrawer,
  toggleCartDrawer,
  addShopifyItem,
  updateShopifyItemQuantity,
  removeShopifyItem,
  clearShopifyCart,
  initShopifyStorefrontElementsInternal,
  selectProductVariantInternal,
  initProductVariantSelectorsInternal,
  initShopifyAppBlocksInternal,
  setCartCurrencyInternal,
  syncStorefrontCartInternal,
  afUpdateInventoryLevel,
  updateInventoryLevel,
  afGetInventoryLevel,
  getInventoryLevel,
  afProcessInventoryWebhook,
  processInventoryWebhook,
  afSubscribeInventoryUpdates,
  subscribeInventoryUpdates,
  updateVariantInventory,
  getVariantInventory,
  processShopifyInventoryWebhook,
  subscribeInventoryDeprecation,
  syncInventoryToDom,
  SUPPORTED_STOREFRONT_CURRENCIES,
  convertStorefrontPrice,
  formatStorefrontPrice,
  getStorefrontCurrencyConfig,
  syncCartWithStorefrontApi,
  initShopifyCustomerSsoInternal,
  afInitShopifyCustomerSso,
  initShopifyCustomerSso,
  afGetCustomerSession,
  getCustomerSession,
  afLoginShopifyCustomer,
  loginShopifyCustomer,
  afLogoutShopifyCustomer,
  logoutShopifyCustomer,
  afSubscribeCustomerAuth,
  subscribeCustomerAuth,
  afSyncCustomerToOrchardCore,
  syncCustomerToOrchardCore,
  buildShopifyCustomerAuthUrl,
  authenticateWithShopifyCustomerToken,
  getStoredCustomerSession,
  clearCustomerSession,
  syncCustomerAccountToDom,
  DEFAULT_CUSTOMER_IDENTITY,
};

